/** Загрузка иконки категории (PNG/JPG/WebP, max 2 MB).
 *
 * POST /categories/:id/icon уже был реализован на бэкенде (см.
 * app/api/admin/categories.py) — не хватало только формы загрузки,
 * вкладка «Иконка» показывала заглушку без возможности что-либо сделать.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
  categoryId?: number;
  iconUrl: string | null;
}

export function CategoryIconUploader({ categoryId, iconUrl }: Props): JSX.Element {
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/categories/${categoryId}/icon`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Иконка загружена");
      queryClient.invalidateQueries({ queryKey: ["admin-category", categoryId] });
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Не удалось загрузить иконку");
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name}: > 2 МБ`);
        return;
      }
      uploadMutation.mutate(file);
    },
    [uploadMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    maxSize: 2 * 1024 * 1024,
    maxFiles: 1,
    disabled: !categoryId || uploadMutation.isPending,
  });

  if (!categoryId) {
    return (
      <p className="text-sm text-muted-foreground">
        Иконку можно загрузить после первого сохранения категории.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {iconUrl && (
        <div className="space-y-1">
          <img
            src={iconUrl}
            alt="Иконка категории"
            className="w-24 h-24 object-cover rounded-md border"
          />
          <p className="text-xs text-muted-foreground break-all">{iconUrl}</p>
        </div>
      )}

      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50",
        )}
      >
        <input {...getInputProps()} />
        <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
        {uploadMutation.isPending ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Загрузка…
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {isDragActive
              ? "Отпустите файл"
              : iconUrl
                ? "Перетащите файл или кликните, чтобы заменить иконку"
                : "Перетащите файл или кликните, чтобы загрузить иконку"}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">PNG / JPG / WebP, макс. 2 МБ</p>
      </div>
    </div>
  );
}
